import type { ProviderDetail } from '../../../../../js/api';
import { Badge } from '../../../components/badge';
import { StatusPill } from '../../../components/StatusPill';
import { WorkflowScopes } from '../../../constants/workflows';
import { formatDateTo24Hr } from '../../../utils/date';
import { EMPTY_VALUE, ProviderDrawerMaps, textOrDash, yesNo } from './providerDisplay';
import { FieldSection, RecordCard, RecordSection } from './ProviderDrawerPrimitives';

const REFERENCE_DATE_FORMAT = 'MMM d, yyyy';

/** Professional links and references — the old "Professional Traits" tab (no letter downloads). */
export function ProfessionalTraitsTab({
  provider,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const references = provider.references || [];

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <FieldSection
        title="Professional Links"
        fields={[
          {
            label: 'LinkedIn',
            value: provider.linkedin_link ? (
              <a
                href={provider.linkedin_link}
                target="_blank"
                rel="noopener noreferrer"
                className="tw:text-brand-green-0 tw:hover:underline tw:break-all"
              >
                {provider.linkedin_link}
              </a>
            ) : (
              EMPTY_VALUE
            ),
          },
          { label: 'CV on file', value: yesNo(provider.has_cv) },
        ]}
      />
      <RecordSection
        title="References"
        count={references.length}
        emptyText="No references registered."
        data-testid="provider-references"
      >
        {references.map((reference, index) => {
          const isTerminated = Boolean(reference.terminated_at);
          return (
            <RecordCard
              key={reference.id}
              muted={isTerminated}
              title={
                <span className="tw:flex tw:items-center tw:gap-2">
                  Reference #{index + 1}
                  {isTerminated && <Badge color="zinc">Terminated</Badge>}
                </span>
              }
              rightElement={
                reference.current_status ? (
                  <StatusPill
                    scope={WorkflowScopes.ProviderReference}
                    status={reference.current_status}
                    small
                  />
                ) : null
              }
              fields={[
                { label: 'Full Name', value: textOrDash(reference.full_name) },
                { label: 'Title', value: textOrDash(reference.title) },
                {
                  label: 'Email',
                  value: reference.email ? (
                    <a
                      href={`mailto:${reference.email}`}
                      className="tw:text-brand-green-0 tw:hover:underline"
                    >
                      {reference.email}
                    </a>
                  ) : (
                    EMPTY_VALUE
                  ),
                },
                { label: 'Phone', value: textOrDash(reference.phone_number) },
                {
                  label: 'Effective',
                  value:
                    formatDateTo24Hr(reference.effective_at, REFERENCE_DATE_FORMAT) || EMPTY_VALUE,
                },
                ...(isTerminated
                  ? [
                      {
                        label: 'Terminated',
                        value:
                          formatDateTo24Hr(reference.terminated_at, REFERENCE_DATE_FORMAT) ||
                          EMPTY_VALUE,
                      },
                    ]
                  : []),
              ]}
            />
          );
        })}
      </RecordSection>
    </div>
  );
}
